<template>
    <BaseInput
        v-bind="$attrs"
        v-model="model"
        :label="label"
        :name="name || label">
        <Combobox
            :model-value="selectedOption"
            :multiple="multiSelect"
            :disabled="disabled"
            :nullable="nullable"
            as="div"
            :class="$attrs.class"
            @update:model-value="selectOption($event)">
            <div class="flex items-center justify-between">
                <div
                    v-if="$slots.inputHelper"
                    class="text-sm">
                    <slot name="inputHelper" />
                </div>
            </div>
            <div class="relative">
                <ComboboxButton
                    as="div"
                    class="flex flex-wrap py-1.5 px-3 pr-10 group w-full rounded-md border-0 ring-1 ring-inset ring-gray-300 after:content-[attr(placeholder)] after:absolute"
                    :class="[disabled ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-gray-200 shadow-none dark:ring-gray-400' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-300 shadow-sm focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500']"
                    @focus="showInput = true; focusInput()">
                    <div class="flex gap-1 w-full flex-wrap items-center">
                        <template
                            v-if="multiSelect && selectedOption?.length > 0">
                            <div
                                v-for="option in selectedOption"
                                :key="_get(option, identifier)">
                                <div
                                    class="z-8 truncate max-w-24 flex gap-1 items-center outline outline-1 dark:bg-gray-800 outline-gray-300 dark:outline-gray-400 rounded-full w-fit">
                                    <span
                                        class="text-xs pl-2 truncate"
                                        :class="{'text-gray-500 dark:text-gray-400 cursor-not-allowed' : disabled}">
                                        <template
                                            v-if="additionalDisplayPosition === 'above' && additionalDisplayKey && _get(option, additionalDisplayKey)">
                                            <span class="block text-gray-400">{{
                                                    _get(option, additionalDisplayKey)
                                                }}</span>
                                            {{ _get(option, displayKey) }}
                                        </template>
                                        <template v-else>
                                            {{ _get(option, displayKey) }}
                                            <span
                                                v-if="additionalDisplayKey && _get(option, additionalDisplayKey)"
                                                class="text-xs text-gray-400">
                                                <br>({{ _get(option, additionalDisplayKey) }})
                                            </span>
                                        </template>
                                    </span>
                                    <button
                                        :disabled="disabled"
                                        class="flex items-center content-center bg-gray-200 dark:bg-gray-600 p-1 m-1 rounded-full hover:cursor-pointer disabled:cursor-not-allowed enabled:hover:bg-gray-300 enabled:dark:hover:bg-gray-500"
                                        @focus="showInput = true; focusInput()"
                                        @click.prevent.stop="removeSelectedItem(_get(option, identifier))">
                                        <Icon
                                            name="heroicons:x-mark"
                                            class="h-2 w-2" />
                                    </button>
                                </div>
                            </div>
                        </template>

                        <ComboboxInput
                            ref="input"
                            class="p-0 grow max-w-full sm:text-sm sm:leading-6 border-none dark:border-none bg-transparent dark:bg-transparent focus:ring-0 disabled:cursor-not-allowed"
                            :class="[(!multiSelect && selectedOption) ? 'absolute inset-0 w-full h-full text-transparent dark:text-transparent placeholder-transparent focus:relative focus:text-gray-500 focus:dark:text-gray-400' : '', !showInput ? 'hidden' : '', inputOverridden ? 'hidden group-focus:block group-focus-within:block' : '']"
                            :placeholder="getPlaceholder()"
                            autocomplete="off"
                            @focus="openOptions = true"
                            @change="onType($event.target.value)"
                            @blur="handleBlur" />

                        <input
                            v-if="!multiSelect && selectedOption"
                            ref="selectedOptionFocus"
                            class="absolute inset-0 w-full h-full -z-10 opacity-0 block"
                            @blur="showInput = true">

                        <div
                            v-if="!multiSelect && selectedOption"
                            class="p-0 grow max-w-full sm:text-sm sm:leading-6 border-none dark:border-none bg-transparent dark:bg-transparent disabled:cursor-not-allowed"
                            :class="[showInput && !disabled ? 'block group-focus-within:hidden' : '']">
                            <slot
                                v-bind="selectedOption"
                                name="selectedOption">
                                <template
                                    v-if="additionalDisplayPosition === 'above' && additionalDisplayKey && _get(selectedOption, additionalDisplayKey)">
                                    <span
                                        class="block text-xs text-gray-400"
                                        :class="[{'truncate' : truncate}]">
                                        {{ _get(selectedOption, additionalDisplayKey) }}
                                    </span>
                                    <span :class="[{'truncate' : truncate}]">
                                        {{ _get(selectedOption, displayKey, true) }}
                                    </span>
                                </template>
                                <template v-else>
                                    <span :class="[{'truncate' : truncate}]">
                                        {{ _get(selectedOption, displayKey, true) }}
                                    </span>
                                    <span
                                        v-if="additionalDisplayKey && _get(selectedOption, additionalDisplayKey)"
                                        class="text-xs text-gray-400"
                                        :class="[{'truncate' : truncate}]">
                                        <br>({{ _get(selectedOption, additionalDisplayKey) }})
                                    </span>
                                </template>
                            </slot>
                        </div>
                    </div>

                    <div
                        v-if="nullable && model && !disabled"
                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none cursor-pointer"
                        @click="model = null; clearInput()">
                        <Icon
                            name="heroicons:x-circle"
                            class="h-5 w-5 text-gray-400"
                            aria-hidden="true" />
                    </div>
                    <div
                        v-else-if="!disabled"
                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none cursor-pointer">
                        <Icon
                            name="heroicons:chevron-up-down"
                            class="h-5 w-5 text-gray-400"
                            aria-hidden="true" />
                    </div>
                </ComboboxButton>

                <ComboboxOptions
                    v-if="fetchOnSearch ? query.length > searchThreshold : true"
                    :style="`max-height: ${optionsBoxHeight}`"
                    class="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    :static="openOptions">
                    <ComboboxOption
                        v-if="fetchOnSearch && pending"
                        selected
                        disabled>
                        <li class="flex justify-center overflow-hidden">
                            <LoadingSpinner size="md" />
                        </li>
                    </ComboboxOption>
                    <ComboboxOption
                        v-else-if="filteredOptions.length === 0 || !filteredOptions"
                        disabled
                        as="template">
                        <li
                            class="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-300">
                            <span class="block">
                                <slot name="noResults">
                                    <p class="whitespace-pre-line">{{ noResults }}</p>
                                </slot>
                            </span>
                        </li>
                    </ComboboxOption>
                    <ComboboxOption
                        v-for="option in filteredOptions"
                        v-else
                        :key="_get(option, identifier)"
                        v-slot="{ active, selected }"
                        :disabled="_get(option, disabledKey)"
                        :value="option"
                        as="template">
                        <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-primary text-white' : 'text-gray-900 dark:text-gray-300']">
                            <span class="block">
                                <slot
                                    v-bind="option"
                                    name="option">
                                    <template
                                        v-if="additionalDisplayPosition === 'above' && additionalDisplayKey && _get(option, additionalDisplayKey)">
                                        <span
                                            class="block text-xs text-gray-400"
                                            :class="[{'truncate' : truncate}]">
                                            {{ _get(option, additionalDisplayKey) }}
                                        </span>
                                        <span :class="[{'truncate' : truncate}]">
                                            {{ _get(option, displayKey, true) }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <span :class="[{'truncate' : truncate}]">
                                            {{ _get(option, displayKey, true) }}
                                        </span>
                                        <span
                                            v-if="additionalDisplayKey && _get(option, additionalDisplayKey)"
                                            class="text-xs text-gray-400"
                                            :class="[{'truncate' : truncate}]">
                                            <br>({{ _get(option, additionalDisplayKey) }})
                                        </span>
                                    </template>
                                </slot>
                            </span>

                            <span
                                v-if="selected"
                                :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-primary-500']">
                                <Icon
                                    name="heroicons:check"
                                    class="h-5 w-5"
                                    aria-hidden="true" />
                            </span>
                        </li>
                    </ComboboxOption>
                </ComboboxOptions>
            </div>
        </Combobox>
    </BaseInput>
</template>
<script setup>
import {useEventBus} from "@vueuse/core"
import {cloneDeep} from 'lodash'
import debounce from "lodash/debounce"
import {useNuxtApp} from "nuxt/app"
import {computed, ref} from 'vue'
import {$larafetch} from "@/utils/$larafetch"

const {t: $t} = useI18n()
const notification = useNotification()

const props = defineProps({
    //field
    modelValue: {
        required: false,
        type: [Number, String, Array, Object],
        default: null,
        description: 'The selected option'
    },
    label: {
        required: false,
        type: String,
        default: null,
        description: 'The label of the select'
    },
    name: {
        required: false,
        type: [String, null],
        default: null,
        description: 'The name of the element, required for validation if no label is provided.'
    },
    disabled: {
        type: Boolean,
        default: false,
        description: 'If the select is disabled'
    },
    placeholder: {
        required: false,
        type: String,
        default: '',
        description: 'The placeholder text of the search input'
    },
    multiSelect: {
        type: Boolean,
        default: false,
        description: 'Whether the select should be a multi select'
    },
    nullable: {
        type: Boolean,
        default: false,
        description: 'Whether the select should be nullable'
    },
    componentKey: {
        type: String,
        default: null,
        description: 'A unique key for the component. Needs to be set when using "adoptValue"-EventBus'
    },
    //options
    options: {
        required: false,
        type: Array,
        default: () => [],
        description: 'The options to display in the select if no api route is given'
    },
    displayKey: {
        required: false,
        type: String,
        default: 'name',
        description: 'The key of the value to display in the options'
    },
    additionalDisplayKey: {
        required: false,
        type: String,
        default: null,
        description: 'An additional key of a value to display as a subtext in the options, eg. an id'
    },
    additionalDisplayPosition: {
        required: false,
        type: String,
        default: 'inline',
        description: 'Where to display the additionalDisplayKey value: "inline" shows it in brackets after the display value, "above" shows it as a small line above the display value'
    },
    identifier: {
        required: false,
        type: String,
        default: 'id',
        description: 'The key of the value that should be saved in the modelValue'
    },
    disabledKey: {
        type: String,
        default: 'disabled',
        description: 'The key of the option that handles if the option is disabled'
    },
    truncate: {
        required: false,
        type: Boolean,
        default: false,
        description: 'Truncate the text of the options if they are too long'
    },
    //search
    noResults: {
        required: false,
        type: String,
        default: () => useI18n().t('noResults'),
        description: 'The text to display if no results are found'
    },
    //remote search
    apiRoute: {
        required: false,
        type: String,
        default: null,
        description: 'The api route to fetch the options from'
    },
    fetchOnSearch: {
        required: false,
        type: Boolean,
        default: false,
        description: 'If the options should only be fetched when a search is performed. When set to false, the options will be fetched on mounted'
    },
    filter: {
        required: false,
        type: Array,
        default: () => [],
        description: 'The filter to apply to the api route'
    },
    output: {
        required: false,
        type: String,
        default: 'select-object',
        description: 'The output of the api route'
    },
    params: {
        type: Object,
        default: () => ({}),
        description: 'The params to send to the api route'
    },
    searchThreshold: {
        required: false,
        type: Number,
        default: 2,
        description: 'The threshold of the search input'
    },
    showInaccurateMatches: {
        type: Boolean,
        default: false,
        description: 'If the component should show options that do not match the query exactly'
    },
    allowArbitraryValue: {
        type: Boolean,
        default: false,
        description: 'If the component should allow arbitrary values'
    },
    optionsBoxHeight: {
        type: String,
        default: '15rem',
        description: 'The height of the options box'
    },
    doNotShowSelectedOnSearch: {
        type: Boolean,
        default: false,
        description: 'If the component should not show the selected option in the search results'
    },
    watchModelValue: {
        type: Boolean,
        default: false,
        description: 'If the component should watch for modelValue changes. False by default since it might cause recursive updates'
    }
})

const query = ref('')
const allOptions = ref([])
const selectedOption = ref(null)
const openOptions = ref(false)
const emit = defineEmits(['update:modelValue', 'update:entireObject', 'change'])
const input = ref(null)
const selectedOptionFocus = ref(null)
const showInput = ref(true)

const model = computed({
    get: () => props.modelValue || (props.multiSelect ? [] : null),
    set: (value) => emit('update:modelValue', value)
})

useEventBus('adoptValue').on((event) => {
    if (event.key === props.componentKey) {
        setSelectedOption(event.value)
    }
})

const removeSelectedItem = (itemId) => {
    selectedOption.value = selectedOption.value.filter((option) => _get(option, props.identifier) !== itemId)
}

const inputOverridden = computed(() => {
    return props.multiSelect && selectedOption.value?.length > 0
})

const availableOptions = computed(() => {
    const opts = allOptions.value
    if (props.allowArbitraryValue) {
        const arbitraryOption = {
            [props.identifier]: query.value,
            [props.displayKey]: query.value,
            arbitrary: true
        }
        if (!opts.find(e => _get(e, props.identifier) === _get(arbitraryOption, props.identifier))) {
            opts.push(arbitraryOption)
        }
    }
    return opts
})

const filteredOptions = computed(() => {
    if (props.showInaccurateMatches) return availableOptions.value
    const options = query.value === ""
        ? availableOptions.value
        : availableOptions.value.filter((option) => {
            return _get(option, props.displayKey, true)?.toLowerCase().includes(query.value.toLowerCase())
                || (props.additionalDisplayKey && _get(option, props.additionalDisplayKey)?.toLowerCase().includes(query.value.toLowerCase()))
        })

    if (props.multiSelect && selectedOption.value?.length > 0) {
        selectedOption.value.forEach((option) => {
            if (!options.find((opt) => _get(opt, props.identifier) === _get(option, props.identifier))) {
                options.push(option)
            }
        })
    } else if (!props.multiSelect && selectedOption.value && !options.find((option) => _get(option, props.identifier) === _get(selectedOption.value, props.identifier))) {
        if (!props.doNotShowSelectedOnSearch || query.value === '') {
            options.push(selectedOption.value)
        }
    }

    return options
})

const getPlaceholder = () => {
    if (inputOverridden.value) {
        return ''
    } else if (props.placeholder) {
        return props.placeholder
    } else if (props.apiRoute) {
        return 'Suche nach Name...'
    } else {
        return useI18n().t('pleaseSelect')
    }
}

const onType = (input) => {
    query.value = input
    if (props.fetchOnSearch && props.apiRoute) {
        pending.value = true
        onSearch()
    }
}

const onSearch = debounce(() => {
    if (query.value.length > props.searchThreshold) {
        return fetchOptions(query.value, props.filter, props.output, props.params)
    }
    pending.value = false
}, 500)

onMounted(async () => {
    // make sure all props are initialized
    await nextTick()
    if (props.apiRoute) {
        if (!props.fetchOnSearch) {
            fetchOptions(query.value, props.filter, props.output, props.params)
        } else {
            if (props.modelValue) {
                const filter = [
                    ...props.filter,
                    {
                        name: 'customFilterFind',
                        column: 'customFilterFind',
                        operator: 'find',
                        value: props.modelValue
                    }
                ]
                fetchOptions(query.value, filter, props.output, props.params)
            } else {
                setSelectedOption()
            }
        }
    } else {
        if (props.options.length) {
            setOptions(props.options)
            setSelectedOption()
        }
    }
})

watch(() => selectedOption.value, (value) => {
    if (value) {
        emit('update:modelValue', getOptionIdentifierValue(value))
        emit('update:entireObject', value)
    } else {
        if (props.multiSelect) {
            emit('update:modelValue', [])
        } else {
            emit('update:modelValue', null)
        }
        emit('update:entireObject', {})
    }
})

watch(() => props.modelValue, (newValue) => {
    if (props.watchModelValue) {
        setSelectedOption(newValue)
    }
})

watch(() => props.options, () => {
    if (props.options) {
        setOptions(props.options)
        setSelectedOption()
    }
})

function setOptions(options = []) {
    const newOptions = cloneDeep(options ?? [])
    allOptions.value.forEach((option) => {
        if (newOptions.some((newOption) => _get(newOption, props.identifier) === _get(option, props.identifier))) return
        if (Array.isArray(props.modelValue) && props.modelValue.includes(_get(option, props.identifier))) {
            newOptions.push(option)
        } else if (!Array.isArray(props.modelValue) && props.modelValue === _get(option, props.identifier)) {
            newOptions.push(option)
        }
    })
    allOptions.value = newOptions
}

function setSelectedOption(value = null) {
    if (!value) value = model.value
    if (!props.multiSelect) {
        selectedOption.value = allOptions.value.find((option) => _get(option, props.identifier) === value) ?? null
    } else {
        if (selectedOption.value === null || value === null) selectedOption.value = []
        const possibleSelection = value?.map(identifier => allOptions.value.find((option) => _get(option, props.identifier) === identifier)).filter(Boolean)
        selectedOption.value = possibleSelection ?? []
    }
    return selectedOption.value
}

const pending = ref(false)
const loading = ref(false)

function fetchOptions(search, filter, output, params) {
    pending.value = true
    loading.value = true

    $larafetch(useNuxtApp().$apiRoute(props.apiRoute), {
        method: 'GET',
        params: {
            search: search,
            filter: filter,
            output: output,
            ...params
        }
    }).then(response => {
        if (output === 'select') {
            setOptions(Object.values(response))
        } else {
            setOptions(response.data)
        }
        setSelectedOption()
    }).catch(error => {
        if (error && error.response && error.response.status === 422) {
            notification.error($t('invalidRequestData'))
        } else if (error && error.response && error.response.status === 404) {
            notification.error($t('modelDoesNotExist'))
        }
    }).finally(() => {
        pending.value = false
        loading.value = false
    })
}

async function selectOption(option) {
    await setSelectedOption(getOptionIdentifierValue(option))
    handleSelect()
}

function handleSelect() {
    if (props.multiSelect) {
        input.value.el.value = ''
        onType('')
        setSelectedOption()
    } else {
        openOptions.value = false
        blurInput()
    }
}

function clearInput() {
    onType('')
    selectedOption.value = props.multiSelect ? [] : null
}

function handleBlur() {
    openOptions.value = false
    if (props.modelValue === null) {
        clearInput()
    } else {
        onType('')
        setSelectedOption()
    }
    if (!props.multiSelect && selectedOption.value) {
        showInput.value = false
        selectedOptionFocus.value.focus()
    }
}

function blurInput() {
    if (props.multiSelect) return
    input.value.el.blur()
}

function focusInput(type) {
    input.value.el.focus()
}

function getOptionIdentifierValue(option) {
    if (props.multiSelect) {
        return option.map((option) => _get(option, props.identifier))
    } else {
        return _get(option, props.identifier)
    }
}

// handle all the different ways to get a value from an object
// options can be object or single value type, or an array in multi select displays
// useOption as fallback to option itself if it can be expected to be a single value/string
function _get(option, prop = null, useOption = false) {
    if (!option) return null
    if (Array.isArray(option)) return null
    if (!prop) return useOption ? option : null
    if (useOption || prop === props.identifier) return option[prop] ?? option
    return option[prop] ?? null
}
</script>